/**
 * flush-visual.ts — 同花成牌视觉化纯函数(方案丁·精简:主导花色高亮 + 一枚 n/5 状态条)。
 *
 * 攻 P1 零基础「同花要 5 张」困惑(再验收 P1 看懂 56.7% 主因:scene-041/069/133/031
 * 都是数不清同花色张数)。§0 产品灵魂:极致视觉化,1 秒可感知,不靠冗长文字。
 *
 * **所见即所验**:UI(poker-table 高亮 + 状态条)与验收探针(extract-screens-reaccept /
 * run-personas formatScreen)共用同一个 flushStatusLabel()——persona 模拟只读文本,
 * 纯 CSS 高亮 persona 看不见,必须回落为同一 label 文本才算「看见」。
 *
 * **真实花色门控**:仅接受 parseCards 解出的真实花色(rank+suit);rank-only 记法
 * ("67s" suited 记法 / "T88" rank-only 牌面 / texture 赋的代表花色)一律返回 none——
 * 假花色绝不显示同花状态,防误导新手(代表花色只传达质地,不是真花色)。
 *
 * **河牌门控(c0490b1 副作用修复)**:听牌/萌芽/牌面威胁都是「等下一张」语义,
 * 河牌(5 张公共牌,无牌可等)出这些状态是逻辑矛盾(与 hint「没有下一张」打架)。
 * 故 street 为河牌(river/河牌/河)时只出「已成同花」,部分张数一律 none。
 * flop/turn 有牌可等,draw/sprout/board_threat 正常出。
 *
 * **n/5 白话化**:P1/P3 读不懂「4/5」(已有还是还差?)。label 一律以「已有X张♠」/
 * 「牌面有X张♠」开头,差几张靠白话补全(「再来1张就成同花」/「还差2张」),短句克制。
 *
 * 纯函数、零副作用:只 import ./poker-cards 与 ./hand-strength(已成同花时借其
 * classZh 附坚果/同花顺标签,不重复牌力判定逻辑)。
 */

import { parseCards, SUIT_SYMBOL } from "./poker-cards";
import type { PokerCard } from "./poker-cards";
import { evaluateHandStrength } from "./hand-strength";

/** 真实花色(不含未知 x)。 */
export type Suit = "s" | "h" | "d" | "c";

const SUITS: Suit[] = ["s", "h", "d", "c"];

export type FlushStatus = "made" | "draw" | "board_threat" | "sprout" | "not_yet" | "none";

export type FlushVisual = {
  status: FlushStatus;
  /** 主导花色(仅已成/听牌/威胁/萌芽有值;not_yet/none 无值)。 */
  suit?: Suit;
  /** 主导花色合计张数 n(5 张目标;not_yet/none 无值)。 */
  count?: number;
  /** 状态条文案;none 时为空串(不渲染,避噪);not_yet 时为「尚未成同花」消歧文案。 */
  label: string;
};

/** 每花色合计张数(忽略未知花色 x);四个真实花色恒返回,缺省 0。 */
export function suitHistogram(cards: PokerCard[]): Record<Suit, number> {
  const hist: Record<Suit, number> = { s: 0, h: 0, d: 0, c: 0 };
  for (const c of cards) {
    if (c.suit !== "x") hist[c.suit] += 1;
  }
  return hist;
}

const NONE: FlushVisual = Object.freeze({ status: "none", label: "" });

/** 河牌街判定:street 含 river/河牌/河 → 河牌(5 张公共牌,无牌可等)。 */
function isRiver(street?: string): boolean {
  if (!street) return false;
  return street.toLowerCase().includes("river") || street.includes("河");
}

/** 翻牌/转牌街判定:有牌可等的街,无同花潜力时才出「尚未成同花」消歧条。 */
function isFlopOrTurn(street?: string): boolean {
  if (!street) return false;
  const s = street.toLowerCase();
  return s.includes("flop") || s.includes("turn") || street.includes("翻") || street.includes("转");
}

/**
 * 同花状态标签(UI 与验收探针共用的唯一真值源)。
 *
 * hero+board 合计 → 主导花色(计数最高且 ≥3;并列取 ♠♥♦♣ 序,确定性):
 *  - ≥5 → 已成同花（5张X）(借 evaluateHandStrength classZh 附「坚果同花/同花顺」)
 *  - =4 → 同花听牌：已有4张X，再来1张就成同花(仅 flop/turn)
 *  - =3 且 hero 该花色 0 张 → 牌面有3张X，小心对手同花(专打 133:别人牌面威胁,仅 flop/turn)
 *  - =3 且 hero 含该花色 → 同花萌芽：已有3张X，还差2张(专打 031:背门萌芽,仅 flop/turn)
 *  - <3 且 street 为 flop/turn → not_yet「尚未成同花」(T4 消歧:与标题「同花色手牌」互证)
 *  - 其余 → none(label "",不显示,避噪)
 *
 * 真实花色门控:heroHand 或 board 任一 parseCards 失败/为空(rank-only、记法、翻前)→ none。
 * 河牌门控:street 为河牌时只出「已成同花」(count≥5);draw/sprout/board_threat → none(无牌可等);
 * 河牌 count<3 同样 none(not_yet 只出 flop/turn——河牌「没有下一张」,状态条不介入)。
 */
export function flushStatusLabel(heroHand: string, board: string, street?: string): FlushVisual {
  const hero = parseCards(heroHand);
  const boardCards = parseCards(board);
  if (!hero || hero.length === 0 || !boardCards || boardCards.length === 0) {
    return NONE;
  }

  const hist = suitHistogram([...hero, ...boardCards]);
  let suit: Suit | null = null;
  let count = 0;
  for (const s of SUITS) {
    if (hist[s] > count) {
      suit = s;
      count = hist[s];
    }
  }
  if (suit === null || count < 3) {
    // T4 同花消歧(scene-032:标题「同花色手牌」vs 状态条矛盾):flop/turn 真实花色齐、
    // 无同花潜力 → 「尚未成同花」与标题互证(suited ≠ 已成同花)。河牌/rank-only/翻前仍 none 避噪。
    if (isFlopOrTurn(street)) return { status: "not_yet", label: "尚未成同花" };
    return NONE;
  }

  const sym = SUIT_SYMBOL[suit];
  const heroCount = suitHistogram(hero)[suit];

  if (count >= 5) {
    // 已成同花:附坚果/同花顺细分(classZh),牌力判定不重复造轮子。
    let word = "已成同花";
    if (hero.length === 2) {
      try {
        const r = evaluateHandStrength(heroHand, board);
        if (r.class === "straight_flush") word = r.classZh; // 同花顺/皇家同花顺/五高同花顺（轮子）
        else if (r.classZh === "坚果同花") word = "坚果同花";
      } catch {
        // 异常张数评估失败 → 回落「已成同花」,状态条不阻塞
      }
    }
    return { status: "made", suit, count, label: `${word}（5张${sym}）` };
  }

  // 河牌门控:河牌无牌可等,部分张数(听牌/萌芽/威胁)无意义 → none。
  if (isRiver(street)) return NONE;

  if (count === 4) {
    return { status: "draw", suit, count, label: `同花听牌：已有4张${sym}，再来1张就成同花` };
  }

  // count === 3
  if (heroCount === 0) {
    return { status: "board_threat", suit, count, label: `牌面有3张${sym}，小心对手同花` };
  }
  return { status: "sprout", suit, count, label: `同花萌芽：已有3张${sym}，还差2张` };
}
